import React, { useState } from 'react';
import { Box, Button, Table, TableBody, TableCell, TableRow } from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon, DragIndicator as DragIcon } from '@mui/icons-material';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
  arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { RowContextMenu, ContextMenuOption } from '../ui/RowContextMenu';
import DataTableHeader from '../table/DataTableHeader';
import { useTablePreferences } from '../../hooks/useTablePreferences';
import { wellnessItemEntryColumns } from '../../config/wellnessItemEntryColumns';
import { WellnessItem } from '../../services/wellnessItemService';
import { WellnessItemEntry } from '../../types/wellnessItemEntry';

interface Props {
  entries: WellnessItemEntry[];
  allWellnessItems: WellnessItem[];
  onAdd: () => void;
  onEdit: (entry: WellnessItemEntry) => void;
  onDelete: (entry: WellnessItemEntry) => void;
  onReorder: (entries: WellnessItemEntry[]) => void;
}

const DRAG_HANDLE_WIDTH = 32;

interface SortableRowProps {
  entry: WellnessItemEntry;
  allWellnessItems: WellnessItem[];
  visibleColumns: string[];
  getColumnWidth: (key: string) => number;
  onClick: (e: React.MouseEvent, entry: WellnessItemEntry) => void;
}

const SortableRow: React.FC<SortableRowProps> = ({ entry, allWellnessItems, visibleColumns, getColumnWidth, onClick }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: entry._key ?? String(entry.wellness_item_id ?? ''),
  });

  const item = allWellnessItems.find((w) => w.id === entry.wellness_item_id) ?? null;

  const getCellValue = (key: string): React.ReactNode => {
    switch (key) {
      case 'wellnessItem':
        if (!entry.wellness_item_id) return '—';
        return item ? item.name : <em style={{ color: '#aaa' }}>Unknown item</em>;
      case 'title': return entry.title ?? item?.data?.title ?? '—';
      default: return '—';
    }
  };

  return (
    <TableRow
      ref={setNodeRef}
      sx={{
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
        bgcolor: isDragging ? 'action.hover' : 'inherit',
        cursor: 'pointer',
        '&:hover': { backgroundColor: 'grey.50' },
      }}
      onClick={(e) => onClick(e, entry)}
    >
      <TableCell
        sx={{ width: DRAG_HANDLE_WIDTH, minWidth: DRAG_HANDLE_WIDTH, px: 0.5, cursor: 'grab' }}
        onClick={(e) => e.stopPropagation()}
        {...attributes}
        {...listeners}
      >
        <DragIcon fontSize="small" sx={{ color: 'text.disabled', display: 'block' }} />
      </TableCell>
      {visibleColumns.map((key) => {
        const width = getColumnWidth(key);
        return (
          <TableCell
            key={key}
            sx={{
              width: `${width}px`,
              minWidth: `${width}px`,
              maxWidth: `${width}px`,
              padding: '12px 8px',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {getCellValue(key)}
          </TableCell>
        );
      })}
    </TableRow>
  );
};

const WellnessItemEntriesTab: React.FC<Props> = ({ entries, allWellnessItems, onAdd, onEdit, onDelete, onReorder }) => {
  const [menuPosition, setMenuPosition] = useState<{ top: number; left: number } | null>(null);
  const [selectedEntry, setSelectedEntry] = useState<WellnessItemEntry | null>(null);

  const tablePrefs = useTablePreferences('wellness-item-entries-v4', wellnessItemEntryColumns);
  const visibleColumns = tablePrefs.preferences.columnOrder.filter((key) =>
    tablePrefs.preferences.visibleColumns.includes(key)
  );
  const totalWidth = DRAG_HANDLE_WIDTH + visibleColumns.reduce((sum, key) => sum + tablePrefs.getColumnWidth(key), 0);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));
  const ids = entries.map((e) => e._key ?? String(e.wellness_item_id ?? ''));

  const handleRowClick = (e: React.MouseEvent, entry: WellnessItemEntry) => {
    setSelectedEntry(entry);
    setMenuPosition({ top: e.clientY, left: e.clientX });
  };

  const handleMenuClose = () => {
    setMenuPosition(null);
    setSelectedEntry(null);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      const oldIndex = ids.indexOf(String(active.id));
      const newIndex = ids.indexOf(String(over.id));
      onReorder(arrayMove(entries, oldIndex, newIndex));
    }
  };

  const contextMenuOptions: ContextMenuOption[] = [
    {
      label: 'Edit Item',
      icon: <EditIcon fontSize="small" sx={{ mr: 1 }} />,
      onClick: () => { if (selectedEntry) onEdit(selectedEntry); },
    },
    {
      label: 'Delete',
      icon: <DeleteIcon fontSize="small" sx={{ mr: 1 }} />,
      onClick: () => { if (selectedEntry) onDelete(selectedEntry); },
      color: 'error',
    },
  ];

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'flex-start', mb: 1 }}>
        <Button variant="contained" endIcon={<AddIcon />} size="small" onClick={onAdd} sx={{ gap: 1 }}>
          Add Item
        </Button>
      </Box>
      <Box sx={{ maxHeight: 280, overflowX: 'auto', overflowY: 'auto', border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
        <Table
          style={{
            width: `${totalWidth}px`,
            minWidth: `${totalWidth}px`,
            borderCollapse: 'collapse',
            backgroundColor: '#fff',
            tableLayout: 'fixed',
          }}
        >
          {/* Prepend drag-handle column to the header via a wrapper approach */}
          <DataTableHeader
            columns={[
              { key: '__drag__', label: '', defaultWidth: DRAG_HANDLE_WIDTH, minWidth: DRAG_HANDLE_WIDTH, visible: true, sortable: false, resizable: false },
              ...wellnessItemEntryColumns,
            ]}
            visibleColumns={['__drag__', ...visibleColumns]}
            getColumnWidth={(key) => key === '__drag__' ? DRAG_HANDLE_WIDTH : tablePrefs.getColumnWidth(key)}
            onColumnResize={tablePrefs.updateColumnWidth}
          />
          <TableBody>
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
              <SortableContext items={ids} strategy={verticalListSortingStrategy}>
                {entries.map((entry) => (
                  <SortableRow
                    key={entry._key ?? String(entry.wellness_item_id ?? '')}
                    entry={entry}
                    allWellnessItems={allWellnessItems}
                    visibleColumns={visibleColumns}
                    getColumnWidth={tablePrefs.getColumnWidth}
                    onClick={handleRowClick}
                  />
                ))}
              </SortableContext>
            </DndContext>
            {entries.length === 0 && (
              <TableRow>
                <TableCell colSpan={visibleColumns.length + 1} sx={{ textAlign: 'center', color: 'text.secondary', py: 2 }}>
                  No items added yet
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Box>
      <RowContextMenu
        position={menuPosition}
        open={!!menuPosition}
        options={contextMenuOptions}
        onClose={handleMenuClose}
      />
    </Box>
  );
};

export default WellnessItemEntriesTab;
